import { Injectable, computed, inject, signal } from '@angular/core';
import { dsApi, dsDtos } from '@vectronic/openApi-admin';
import { ReplaySubject, mergeMap, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class DeviceTypeService {
  private deviceTypeApi = inject(dsApi.DeviceTypesAdmin);
  readonly deviceTypes = signal<dsDtos.DeviceType[]>([]);

  cameraDeviceTypes = computed(() => {
    const cameraDeviceTypes = this.deviceTypes()?.filter(
      (dt) => dt.deviceClassWithOwnIdPool === 'CAMERA',
    );
    return this.uniqueNameDeviceTypes(cameraDeviceTypes);
  });

  collarDeviceTypes = computed(() => {
    const filtered = this.deviceTypes()?.filter((dt) => {
      const isCollar = dt.deviceClassWithOwnIdPool === 'COLLAR';
      if (isCollar) return true;
      const name = (dt.customerName || dt.name || '').toLowerCase();
      return name.includes('vertex vision');
    });
    return this.uniqueNameDeviceTypes(filtered);
  });

  airtimeDeviceTypes = computed(() => {
    const filtered = (this.deviceTypes() ?? []).filter(
      (dt) => dt.isLifetimeCalculationAvailable === true,
    );
    return this.uniqueNameDeviceTypes(filtered);
  });

  private uniqueNameDeviceTypes(deviceTypes: dsDtos.DeviceType[] = []) {
    const map = new Map<string, dsDtos.DeviceType>();
    for (const deviceType of deviceTypes) {
      const key = (deviceType.customerName || deviceType.name || '').trim().toLowerCase();
      if (!map.has(key)) map.set(key, deviceType);
    }
    return Array.from(map.values());
  }

  constructor() {
    this.refresh().subscribe();
  }

  private _deviceTypes$ = new ReplaySubject<dsDtos.DeviceType[]>(1);

  deviceTypes$() {
    return this._deviceTypes$;
  }

  refresh() {
    return this.deviceTypeApi.getDeviceTypes().pipe(
      tap((deviceTypes) => {
        this._deviceTypes$.next(deviceTypes);
        this.deviceTypes.set(deviceTypes);
      }),
    );
  }

  add(deviceType: dsDtos.DeviceTypeAdd) {
    return this.deviceTypeApi.addDeviceType(deviceType).pipe(mergeMap(() => this.refresh()));
  }

  edit(id: string, deviceType: dsDtos.DeviceTypeEdit) {
    return this.deviceTypeApi.editDeviceType(id, deviceType).pipe(mergeMap(() => this.refresh()));
  }

  delete(id: string) {
    return this.deviceTypeApi.deleteDeviceType(id).pipe(mergeMap(() => this.refresh()));
  }

  getDeviceTypeParameterConfigs(deviceTypeId: string) {
    return this.deviceTypes().find((dt) => dt.id === deviceTypeId)?.availableParameters ?? [];
  }
}
